/**
 * Video Library taxonomy (sections, subsections, tags).
 *
 * Dev-defined (not editor-managed) so the /dashboard/videos hub layout and
 * filter UI stay stable. Editors pick from these lists via select/multiselect
 * fields in keystatic.config.ts; entries live in content/videos/*.
 */

export type VideoSectionSlug =
  | "wheel"
  | "directional-trading"
  | "agentic-trading";

export type VideoSubsectionSlug =
  | "intro"
  | "practical-examples"
  | "platform-deep-dives";

export type VideoSection = {
  slug: VideoSectionSlug;
  name: string;
  description: string;
  /** Section has no watchable content yet; hub renders a Coming soon card. */
  comingSoon?: boolean;
};

export const VIDEO_SECTIONS: readonly VideoSection[] = [
  {
    slug: "wheel",
    name: "Wheel",
    description:
      "Cash-secured puts, covered calls and the full wheel workflow inside QuantWheel.",
  },
  {
    slug: "directional-trading",
    name: "Directional Trading",
    description:
      "Trade with dealer positioning: GEX, vanna, charm and options flows.",
  },
  {
    slug: "agentic-trading",
    name: "Agentic Trading",
    description:
      "AI research agents and QW Intelligence working your portfolio for you.",
    comingSoon: true,
  },
] as const;

export const VIDEO_SUBSECTIONS: readonly {
  slug: VideoSubsectionSlug;
  name: string;
}[] = [
  { slug: "intro", name: "Intro" },
  { slug: "practical-examples", name: "Practical Examples" },
  { slug: "platform-deep-dives", name: "Platform Deep Dives" },
] as const;

export const VIDEO_SECTION_OPTIONS = VIDEO_SECTIONS.map((s) => ({
  label: s.name,
  value: s.slug,
}));

export const VIDEO_SUBSECTION_OPTIONS = VIDEO_SUBSECTIONS.map((s) => ({
  label: s.name,
  value: s.slug,
}));

export function getVideoSection(slug: string): VideoSection | undefined {
  return VIDEO_SECTIONS.find((s) => s.slug === slug);
}

export function getVideoSubsectionName(slug: string): string {
  return VIDEO_SUBSECTIONS.find((s) => s.slug === slug)?.name ?? slug;
}

/** Filter tags shown as chips on the hub. Append here to add a tag. */
export const VIDEO_TAGS: readonly { slug: string; label: string }[] = [
  { slug: "wheel", label: "Wheel" },
  { slug: "gex", label: "GEX" },
  { slug: "vanna", label: "Vanna" },
  { slug: "charm", label: "Charm" },
  { slug: "flows", label: "Flows" },
  { slug: "portfolio", label: "Portfolio" },
  { slug: "journal", label: "Journal" },
  { slug: "earnings", label: "Earnings" },
  { slug: "ai-agents", label: "AI Agents" },
] as const;

export const VIDEO_TAG_OPTIONS = VIDEO_TAGS.map((t) => ({
  label: t.label,
  value: t.slug,
}));

export function getVideoTagLabel(slug: string): string {
  return VIDEO_TAGS.find((t) => t.slug === slug)?.label ?? slug;
}
